import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { HeaderComponent } from '../../../shared/header/header.component';
import { ResponsavelLegal, ResponsavelModalComponent } from '../../../shared/responsavel-modal/responsavel-modal.component';
import { AtendimentoService } from '../../../../services/atendimento/atendimento.service';
import { PaymentService } from '../../../../services/payment/payment.service';
import { CashRegisterService } from '../../../../services/cash-register/cash-register.service';
import { PaymentHubService } from '../../../../services/payment-hub/payment-hub.service';
import { LocalStorageService } from '../../../../services/local-storage/local-storage.service';
import { BrandService } from '../../../../services/brand/brand.service';
import { CashRegister } from '../../../../interfaces/cash-register';
import { PatientAdvance, PostCashPaymentRequest } from '../../../../interfaces/payment';
import { PacienteBusca } from '../../../../interfaces/dados-pagamento';
import {
  adiantamentoEstornado,
  ehDeHoje,
  EstadoCobranca,
  DIAS_HISTORICO_ADIANTAMENTOS,
  ESPERAS_BUSCA_ADIANTAMENTO_MS,
  extrairDocumentoDoReference,
  paginaValida,
  proximoEstado,
  saldoIntacto,
  TAMANHO_PAGINA_ADIANTAMENTOS,
  TIMEOUT_TERMINAL_MS,
  totalPaginas,
} from '../../../../utils/pagamento-avulso.util';
import {
  parcelaMinima,
  parcelaValida,
  parcelasDisponiveis as calcularParcelasDisponiveis,
} from '../../../../utils/parcelamento.util';
import {
  filtrarTerminaisPorTipo,
  montarTerminalFull,
  serialInicialPorTipo,
  TerminalAdyen,
} from '../../../../utils/maquineta.util';
import { ComprovanteCartao, montarComprovanteCartao } from '../../../../utils/comprovante-avulso.util';
import { ComprovanteEstorno, montarComprovanteEstorno } from '../../../../utils/comprovante-estorno.util';
import {
  ComprovanteTransferencia,
  montarComprovanteTransferencia,
  TermoTransferenciaImpresso,
  montarTermoTransferencia,
} from '../../../../utils/comprovante-transferencia.util';
import { montarComprovanteEspecie } from '../../../../utils/comprovante-especie.util';
import { montarReimpressaoCartaoPix } from '../../../../utils/comprovante-reimpressao-cartao.util';
import { formatBRL, openPrintWindow, openTermoWindow } from '../../../../utils/print-receipt.util';
import { base64ParaBlobUrl } from '../../../../utils/abrir-pdf.util';

const DEBOUNCE_BUSCA_MS = 400;
const LIMITE_SANGRIA_CAIXA = 1000;

@Component({
  selector: 'app-pagamento-avulso',
  standalone: true,
  imports: [CommonModule, FormsModule, HeaderComponent, RouterLink, ResponsavelModalComponent],
  // Sem modo escuro proprio: fica no tema claro mesmo com o app escuro.
  host: { class: 'tema-claro', style: 'display: contents' },
  templateUrl: './pagamento-avulso.component.html',
})
export class PagamentoAvulsoComponent implements OnInit, OnDestroy {
  // Embutido: a tela roda dentro do dashboard, sem header nem breadcrumb, e
  // nenhuma guarda pode navegar — sair da rota derruba o hub da fila.
  @Input() embutido = false;
  /** Paciente ja escolhido (senha chamada no dashboard); sem ele a tela abre na busca. */
  @Input() pacienteInicial: PacienteBusca | null = null;
  @Output() fechado = new EventEmitter<string | null>();

  estado: EstadoCobranca = 'ocioso';
  termoBusca = '';
  resultadosBusca: PacienteBusca[] = [];
  paciente: PacienteBusca | null = null;
  isBuscando = false;

  metodo: 1 | 2 | 3 | 4 = 1;
  valor: number | null = null;
  parcelas = 1;
  maquinetaTerminalTipo = 'V240mPlus';
  maquinetaTerminalNumero = '';
  maquinetaTerminalFull = '';
  maquinetaTerminaisFiltrados: TerminalAdyen[] = [];
  showTerminalDropdown = false;

  referenceId: string | null = null;
  mensagem = '';
  erro = '';
  adiantamentos: PatientAdvance[] = [];
  isConferindo = false;
  isEnviando = false;

  caixas: CashRegister[] = [];
  caixaSelecionada: CashRegister | null = null;
  // Cartão e Pix não passam pelo caixa; só o Dinheiro precisa dele.
  semCaixa = false;
  observacao = '';
  isSubmittingCaixa = false;
  isAbrindoSaldo = false;
  saldoAbertoFeedback = '';
  reciboResultado: { nrRecibo: number; valor: number } | null = null;
  idempotencyKey = '';

  isImprimindoComprovante = false;
  comprovanteErro = '';
  erroAdiantamentos = '';
  adiantamentoConfirmado: PatientAdvance | null = null;
  isLocalizandoAdiantamento = false;
  // Snapshot do que foi enviado: o formulario segue editavel depois da cobranca.
  private cobrancaEnviada: { metodo: string; parcelas: number; terminal: string } | null = null;

  isSupervisor = false;
  estornoAlvo: PatientAdvance | null = null;
  isEstornando = false;
  erroEstorno = '';
  // Capturado antes do recarregamento: o back exclui IE_STATUS = 'P' da lista.
  ultimoComprovanteEstorno: ComprovanteEstorno | null = null;
  isImprimindoComprovanteEstorno = false;
  // O banco leva de segundos a minutos para refletir o refund.
  private estornadosConfirmados = new Set<number>();
  comprovanteEstornoErro = '';

  paginaAdiantamentos = 1;

  transferenciaAlvo: PatientAdvance | null = null;
  transferenciaTermo = '';
  transferenciaResultados: PacienteBusca[] = [];
  transferenciaDestino: PacienteBusca | null = null;
  isBuscandoDestino = false;
  isTransferindo = false;
  erroTransferencia = '';
  private transferenciaTimer: any = null;
  // Transferidos nesta sessão: o mesmo motivo do estorno — a lista pode demorar a refletir.
  private transferidosConfirmados = new Set<number>();
  ultimoComprovanteTransferencia: ComprovanteTransferencia | null = null;
  comprovanteTransferenciaErro = '';
  termoArquivo: File | null = null;
  termoAnexado = false;
  isAnexandoTermo = false;
  erroTermo = '';
  isBuscandoTermo = false;
  buscandoTermoAlvo: number | null = null;
  erroTermoConsulta = '';
  // Fora do try/finally de isBuscandoTermo: precisa sobreviver ao reset para o template saber a qual linha o erro pertence.
  erroTermoAlvo: number | null = null;

  // reimprimindoAlvo identifica a linha em andamento, para o spinner sair só nela.
  isReimprimindo = false;
  reimprimindoAlvo: number | null = null;
  erroReimpressao = '';

  showModalResponsavel = false;
  respLegalSalvo = false;
  respLegal: ResponsavelLegal | null = null;

  abrirModalResponsavel(): void {
    this.showModalResponsavel = true;
  }

  onResponsavelCarregado(e: { responsavel: ResponsavelLegal; completo: boolean }): void {
    this.respLegal = e.responsavel;
    this.respLegalSalvo = e.completo;
  }

  onResponsavelSalvo(r: ResponsavelLegal): void {
    this.respLegal = r;
    this.respLegalSalvo = true;
  }

  get isSuperClinica(): boolean {
    return this.brandService.cdEmpresa === '16';
  }

  get opcoesParcelas(): number[] {
    return calcularParcelasDisponiveis(this.valor || 0, this.isSuperClinica);
  }

  get parcelaMinimaAtual(): number {
    return parcelaMinima(this.isSuperClinica);
  }

  // O valor é digitado: baixá-lo pode apagar a opção já escolhida.
  onValorChange(): void {
    this.parcelas = parcelaValida(this.parcelas, this.valor || 0, this.isSuperClinica);
  }

  private username = '';
  private clinicId = 0;
  private timeoutHandle: any = null;
  private inscricaoHub?: Subscription;
  private buscaTimer: any = null;
  // token invalida rodadas antigas; timer/resolver cancelam a espera no ngOnDestroy.
  private buscaComprovanteToken = 0;
  private buscaComprovanteTimer: any = null;
  private buscaComprovanteResolver: (() => void) | null = null;

  constructor(
    private router: Router,
    private atendimentoService: AtendimentoService,
    private paymentService: PaymentService,
    private cashRegisterService: CashRegisterService,
    private paymentHub: PaymentHubService,
    private localStorage: LocalStorageService,
    private brandService: BrandService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.username = this.localStorage.get('login') as string;
    this.clinicId = Number(this.localStorage.get('currentUnit'));

    if (!this.username || !this.clinicId) {
      this.recusar('Sessão sem usuário ou unidade. Faça login novamente.', '/home');
      return;
    }

    // Esconder o card não basta: a rota é alcançável pela URL.
    if (!(await this.paymentService.getPagamentoAvulsoLiberado(this.clinicId))) {
      this.recusar('Pagamento avulso não está liberado para esta unidade.');
      return;
    }

    this.caixas = await this.cashRegisterService.getByUnit(this.clinicId, this.username);
    this.caixaSelecionada = this.caixas[0] ?? null;
    this.semCaixa = this.caixas.length === 0;
    if (this.semCaixa && this.metodo === 4) this.metodo = 1;

    // Estornar desfaz dinheiro — trava mais rígida que "ter caixa na unidade" (critério da tela toda).
    const perfil = await this.cashRegisterService.getUserProfile(this.username);
    this.isSupervisor = !!(perfil?.isSupervisorGlobal || perfil?.isSupervisorUnit);

    const terminalSalvo = localStorage.getItem('adyen_terminal_id');
    if (terminalSalvo) {
      this.maquinetaTerminalFull = terminalSalvo;
      const partes = terminalSalvo.split('-');
      if (partes.length >= 2) {
        this.maquinetaTerminalTipo = partes[0];
        this.maquinetaTerminalNumero = partes.slice(1).join('-');
      }
    }

    // So depois das travas (unidade liberada, caixa): antes disso a tela pode ser recusada.
    if (this.pacienteInicial) this.selecionarPaciente(this.pacienteInicial);
  }

  ngOnDestroy(): void {
    this.limparTimeout();
    if (this.buscaTimer) clearTimeout(this.buscaTimer);
    this.pararBuscaAdiantamento();
    this.inscricaoHub?.unsubscribe();
    this.paymentHub.desconectar();
  }

  // ── Paciente ───────────────────────────────────────────────

  onTermoBuscaChange(): void {
    if (this.buscaTimer) clearTimeout(this.buscaTimer);

    const termo = this.termoBusca.trim();
    if (termo.length < 3) {
      this.resultadosBusca = [];
      return;
    }

    this.buscaTimer = setTimeout(async () => {
      this.isBuscando = true;
      try {
        this.resultadosBusca = await this.atendimentoService.buscarPaciente(termo);
      } finally {
        this.isBuscando = false;
      }
    }, DEBOUNCE_BUSCA_MS);
  }

  selecionarPaciente(p: PacienteBusca): void {
    this.paciente = p;
    this.termoBusca = '';
    this.resultadosBusca = [];
    this.recarregarAdiantamentosSilencioso();
  }

  trocarPaciente(): void {
    this.limparTimeout();
    this.inscricaoHub?.unsubscribe();
    this.paymentHub.desconectar();
    this.pararBuscaAdiantamento();

    this.paciente = null;
    this.termoBusca = '';
    this.resultadosBusca = [];
    this.respLegalSalvo = false;
    this.respLegal = null;
    this.estado = proximoEstado(this.estado, 'reset');
    this.referenceId = null;
    this.adiantamentoConfirmado = null;
    this.cobrancaEnviada = null;
    this.mensagem = '';
    this.erro = '';
    this.adiantamentos = [];
    this.paginaAdiantamentos = 1;
    this.estornadosConfirmados.clear();
    this.erroAdiantamentos = '';
    this.comprovanteErro = '';
    this.ultimoComprovanteEstorno = null;
    this.comprovanteEstornoErro = '';
    this.transferidosConfirmados.clear();
    this.ultimoComprovanteTransferencia = null;
    this.comprovanteTransferenciaErro = '';
    // Mesmo nrAdiantamento pode aparecer na tela do destinatário: sem isso o selo vermelho
    // reaparece sob outra linha, sem o operador ter clicado em nada.
    this.erroTermoConsulta = '';
    this.erroTermoAlvo = null;
    this.fecharTransferencia();

    // Um reenvio acidental gravaria o recebimento no nome de outra pessoa.
    this.valor = null;
    this.observacao = '';
    this.idempotencyKey = '';
    this.reciboResultado = null;
  }

  // ── Terminal ───────────────────────────────────────────────

  selecionarTerminalTipo(tipo: string): void {
    this.maquinetaTerminalTipo = tipo;
    this.maquinetaTerminalNumero = serialInicialPorTipo(tipo);
    this.maquinetaTerminalFull = '';
    this.filtrarTerminais();
  }

  filtrarTerminais(): void {
    const termo = this.maquinetaTerminalNumero.trim();
    this.maquinetaTerminaisFiltrados = filtrarTerminaisPorTipo(this.maquinetaTerminalTipo, termo);
    this.showTerminalDropdown = this.maquinetaTerminaisFiltrados.length > 0;
    this.maquinetaTerminalFull = montarTerminalFull(this.maquinetaTerminalTipo, termo);
  }

  selecionarTerminal(t: TerminalAdyen): void {
    this.maquinetaTerminalNumero = t.numero;
    this.maquinetaTerminalFull = `${t.tipo}-${t.numero}`;
    this.showTerminalDropdown = false;
    localStorage.setItem('adyen_terminal_id', this.maquinetaTerminalFull);
  }

  // ── Cobrança ───────────────────────────────────────────────

  formValido(): boolean {
    return !!this.paciente && !!this.valor && this.valor > 0 && !!this.maquinetaTerminalFull;
  }

  get podeCobrar(): boolean {
    return this.formValido() && (this.estado === 'ocioso' || this.estado === 'erro' || this.estado === 'confirmado');
  }

  async cobrar(): Promise<void> {
    if (!this.formValido() || this.isEnviando || this.estado === 'aguardando') return;

    // Cancela inscrição/timeout anteriores: o callback velho ainda muda a tela atual.
    this.inscricaoHub?.unsubscribe();
    this.limparTimeout();
    this.pararBuscaAdiantamento();

    this.isEnviando = true;
    this.estado = proximoEstado(this.estado, 'enviar');
    this.erro = '';
    this.mensagem = '';
    this.comprovanteErro = '';
    this.referenceId = null;
    this.adiantamentoConfirmado = null;
    const parcelasEnviadas = this.metodo === 1 ? this.parcelas : 1;
    this.cobrancaEnviada = {
      metodo: this.metodoLabel,
      parcelas: parcelasEnviadas,
      terminal: this.maquinetaTerminalFull,
    };
    localStorage.setItem('adyen_terminal_id', this.maquinetaTerminalFull);

    try {
      await this.paymentHub.conectar(this.paciente!.cdPessoaFisica);
      this.inscricaoHub = this.paymentHub.resultado$.subscribe(r => {
        this.limparTimeout();
        this.estado = proximoEstado(this.estado, r.sucesso ? 'sucesso' : 'falha');
        this.mensagem = r.mensagem;
        if (r.sucesso) {
          this.identificarAdiantamentoConfirmado();
          this.recarregarAdiantamentosSilencioso();
        }
      });

      const resposta = await this.atendimentoService.cobrarMaquineta({
        metodo: this.metodo,
        valor: this.valor!,
        parcelas: parcelasEnviadas,
        terminal: this.maquinetaTerminalFull,
        descricao: 'Pagamento avulso',
        patientId: this.paciente!.cdPessoaFisica,
        cdEstabelecimento: this.clinicId,
        isAvulsoFila: true,
      });

      this.referenceId = resposta?.referenceId ?? null;
      this.estado = proximoEstado(this.estado, 'enviado');
      this.timeoutHandle = setTimeout(() => {
        this.estado = proximoEstado(this.estado, 'timeout');
      }, TIMEOUT_TERMINAL_MS);
    } catch (e: any) {
      if (e?.status === 504) {
        // Pode ter cobrado: mantem a inscricao do hub viva, confirmacao atrasada ainda vale.
        this.referenceId = e?.error?.referenceId ?? e?.error?.data?.[0]?.referenceId ?? null;
        this.estado = proximoEstado(this.estado, 'timeout');
        this.mensagem = 'O terminal não respondeu a tempo.';
      } else {
        this.estado = proximoEstado(this.estado, 'falha');
        this.erro = e?.error?.error || e?.error?.feedback || e?.message || 'Erro ao enviar para a maquineta.';
        await this.paymentHub.desconectar();
      }
    } finally {
      this.isEnviando = false;
    }
  }

  async conferirPagamento(): Promise<void> {
    if (!this.paciente || !this.referenceId || this.isConferindo) return;

    const documento = extrairDocumentoDoReference(this.referenceId);
    if (!documento) return;

    this.isConferindo = true;
    this.erro = '';
    try {
      // Janela de 1 dia so para achar o documento - nao e a lista de 30 dias exibida.
      const lista = await this.paymentService.getPatientAdvances(String(this.paciente.cdPessoaFisica), 1);
      const encontrado = lista.find(a => a.nrDocumento === documento) ?? null;
      this.estado = proximoEstado(this.estado, encontrado ? 'conferencia-achou' : 'conferencia-nao-achou');
      this.mensagem = encontrado
        ? 'Pagamento localizado no adiantamento do paciente.'
        : 'Ainda não encontramos esse pagamento. Confira o terminal antes de cobrar de novo.';
      if (encontrado) this.adiantamentoConfirmado = encontrado;
      await this.recarregarAdiantamentosSilencioso();
    } catch {
      // Falha de conferencia nao e resultado de conferencia.
      this.erro = 'Não foi possível conferir agora. Tente novamente.';
    } finally {
      this.isConferindo = false;
    }
  }

  // Corrida provada em produção: o hub confirma quando o terminal aprova, mas o adiantamento
  // só é gravado depois, pelo serviço de pagamentos — daí as retentativas.
  private async identificarAdiantamentoConfirmado(): Promise<void> {
    if (!this.paciente || !this.referenceId) return;
    const documento = extrairDocumentoDoReference(this.referenceId);
    if (!documento) return;

    const referenceId = this.referenceId;
    const cdPessoaFisica = this.paciente.cdPessoaFisica;

    this.pararBuscaAdiantamento();
    const token = this.buscaComprovanteToken;
    this.isLocalizandoAdiantamento = true;

    try {
      for (const espera of ESPERAS_BUSCA_ADIANTAMENTO_MS) {
        await this.aguardar(espera);
        // Só continua enquanto for a mesma cobrança.
        if (token !== this.buscaComprovanteToken || this.referenceId !== referenceId) return;

        try {
          const lista = await this.paymentService.getPatientAdvances(String(cdPessoaFisica), 1);
          if (token !== this.buscaComprovanteToken || this.referenceId !== referenceId) return;
          const encontrado = lista.find(a => a.nrDocumento === documento) ?? null;
          if (encontrado) {
            this.adiantamentoConfirmado = encontrado;
            // A lista foi carregada antes de o adiantamento existir.
            await this.recarregarAdiantamentosSilencioso();
            return;
          }
        } catch {
        }
      }
    } finally {
      if (token === this.buscaComprovanteToken) this.isLocalizandoAdiantamento = false;
    }
  }

  private aguardar(ms: number): Promise<void> {
    return new Promise(resolve => {
      this.buscaComprovanteResolver = resolve;
      this.buscaComprovanteTimer = setTimeout(() => {
        this.buscaComprovanteResolver = null;
        this.buscaComprovanteTimer = null;
        resolve();
      }, ms);
    });
  }

  private pararBuscaAdiantamento(): void {
    this.buscaComprovanteToken++;
    if (this.buscaComprovanteTimer) {
      clearTimeout(this.buscaComprovanteTimer);
      this.buscaComprovanteTimer = null;
    }
    if (this.buscaComprovanteResolver) {
      const resolver = this.buscaComprovanteResolver;
      this.buscaComprovanteResolver = null;
      resolver();
    }
    this.isLocalizandoAdiantamento = false;
  }

  // ── Método de pagamento ───────────────────────────────────────

  // Único lugar que limpa reciboResultado, sempre por clique do operador.
  selecionarMetodo(metodo: 1 | 2 | 3 | 4): void {
    if (metodo === 4 && this.semCaixa) return;
    this.limparTimeout();
    this.inscricaoHub?.unsubscribe();
    this.paymentHub.desconectar();
    this.pararBuscaAdiantamento();

    this.metodo = metodo;
    if (metodo !== 1) this.parcelas = 1;
    this.erro = '';
    this.estado = proximoEstado(this.estado, 'reset');
    this.mensagem = '';
    this.reciboResultado = null;
    this.comprovanteErro = '';
    if (metodo === 4) {
      this.idempotencyKey = this.novaIdempotencyKey();
    }
  }

  private novaIdempotencyKey(): string {
    return typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `especie-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  // ── Espécie (Caixa) ────────────────────────────────────────

  especieFormValido(): boolean {
    // O back nao deduplica: a chave so trava clique duplo em voo.
    if (this.reciboResultado) return false;
    if (!this.paciente) return false;
    if (!this.caixaSelecionada?.balanceSequence) return false;
    if (!this.caixaSelecionada.balanceForToday) return false;
    if (!this.valor || this.valor <= 0) return false;
    return true;
  }

  get saldoCaixaProjetado(): number {
    return (this.caixaSelecionada?.currentBalance ?? 0) + (this.valor ?? 0);
  }

  get precisaSangria(): boolean {
    return !!this.caixaSelecionada && (this.valor ?? 0) > 0 && this.saldoCaixaProjetado > LIMITE_SANGRIA_CAIXA;
  }

  async abrirSaldoHoje(): Promise<void> {
    if (!this.caixaSelecionada || this.isAbrindoSaldo) return;

    this.isAbrindoSaldo = true;
    this.erro = '';
    this.saldoAbertoFeedback = '';
    try {
      // Revalida antes de abrir — evita duplicar saldo quando outra aba/PC já abriu.
      await this.carregarCaixasUsuario();
      const atual = this.caixas.find(c => c.sequence === this.caixaSelecionada!.sequence);
      if (atual) this.caixaSelecionada = atual;

      if (this.caixaSelecionada.balanceForToday && this.caixaSelecionada.balanceSequence) {
        this.saldoAbertoFeedback = `Saldo de hoje já está aberto (seq ${this.caixaSelecionada.balanceSequence}).`;
        return;
      }

      const result = await this.cashRegisterService.openBalance({
        cashRegisterSequence: this.caixaSelecionada.sequence,
        username: this.username,
      });
      await this.carregarCaixasUsuario();
      const refreshed = this.caixas.find(c => c.sequence === this.caixaSelecionada!.sequence);
      if (refreshed) this.caixaSelecionada = refreshed;
      this.saldoAbertoFeedback = `Saldo aberto com sucesso (seq ${result.balanceSequence}).`;
    } catch (e: any) {
      this.erro = e?.error?.feedback || e?.message || 'Erro ao abrir saldo do caixa.';
    } finally {
      this.isAbrindoSaldo = false;
    }
  }

  async pagarEspecie(): Promise<void> {
    if (!this.especieFormValido() || this.isSubmittingCaixa) return;

    this.isSubmittingCaixa = true;
    this.erro = '';
    try {
      // Chave vazia viraria uma chave so para todos os envios.
      if (!this.idempotencyKey) this.idempotencyKey = this.novaIdempotencyKey();

      const request: PostCashPaymentRequest = {
        nrSeqSaldoCaixa: this.caixaSelecionada!.balanceSequence!,
        patientId: String(this.paciente!.cdPessoaFisica),
        amount: this.valor!,
        establishmentId: this.caixaSelecionada!.establishmentId,
        username: this.username,
        observation: this.observacao?.trim() || 'Pagamento avulso — Fila Inteligente',
      };

      const result = await this.paymentService.postCashPayment(this.idempotencyKey, request);
      this.reciboResultado = { nrRecibo: result.nrRecibo, valor: this.valor! };
      this.estado = proximoEstado(this.estado, 'sucesso');
      await this.recarregarAdiantamentosSilencioso();
    } catch (e: any) {
      const status = e?.status;
      if (status === 0) this.erro = 'Sem conexão. Verifique a rede e tente novamente.';
      else if (status === 401 || status === 403) this.erro = 'Sessão expirada. Faça login novamente.';
      else this.erro = e?.error?.feedback || e?.message || 'Erro ao registrar pagamento em espécie.';
    } finally {
      this.isSubmittingCaixa = false;
    }
  }

  private async carregarCaixasUsuario(): Promise<void> {
    this.caixas = await this.cashRegisterService.getByUnit(this.clinicId, this.username);
  }

  // Propaga o erro de propósito: falha de rede não pode se disfarçar de "sem adiantamento".
  // Um selo só por linha, em vez de quatro condições concorrendo no template.
  situacaoAdiantamento(a: PatientAdvance): { texto: string; fundo: string; cor: string } {
    if (this.estaEstornado(a)) return { texto: 'Estornado', fundo: '#fef2f2', cor: '#991b1b' };
    if (this.foiTransferido(a)) return { texto: 'Transferido', fundo: '#eef2ff', cor: '#3730a3' };
    if (this.saldoExibido(a) > 0) return { texto: 'Disponível', fundo: '#dcfce7', cor: '#166534' };
    return { texto: 'Utilizado', fundo: '#e5e7eb', cor: '#374151' };
  }

  // Estornado e transferido ficam de fora: nenhum dos dois é saldo que este paciente pode gastar.
  get totalDisponivel(): number {
    return this.adiantamentos
      .filter(a => !this.estaEstornado(a) && !this.foiTransferido(a))
      .reduce((soma, a) => soma + this.saldoExibido(a), 0);
  }

  get totalPaginasAdiantamentos(): number {
    return totalPaginas(this.adiantamentos.length);
  }

  get adiantamentosPagina(): PatientAdvance[] {
    const inicio = (this.paginaAdiantamentos - 1) * TAMANHO_PAGINA_ADIANTAMENTOS;
    return this.adiantamentos.slice(inicio, inicio + TAMANHO_PAGINA_ADIANTAMENTOS);
  }

  irParaPagina(n: number): void {
    this.paginaAdiantamentos = paginaValida(n, this.adiantamentos.length);
  }

  trackAdiantamento(_: number, a: PatientAdvance): number {
    return a.nrAdiantamento;
  }

  async carregarAdiantamentos(): Promise<void> {
    if (!this.paciente) return;
    this.adiantamentos = await this.paymentService.getPatientAdvances(String(this.paciente.cdPessoaFisica), DIAS_HISTORICO_ADIANTAMENTOS);
    // A lista pode encolher entre um carregamento e outro: sem isso a tela fica numa página
    // que não existe mais, vazia.
    this.irParaPagina(this.paginaAdiantamentos);
  }

  private async recarregarAdiantamentosSilencioso(): Promise<void> {
    try {
      await this.carregarAdiantamentos();
      this.erroAdiantamentos = '';
    } catch {
      this.erroAdiantamentos = 'Não foi possível carregar os adiantamentos do paciente.';
    }
  }

  // ── Transferência de saldo ─────────────────────────────────

  podeTransferir(a: PatientAdvance): boolean {
    if (!this.isSupervisor) return false;
    // Quem so recebeu o saldo nao redistribui: a regra de vinculo e sempre contra quem pagou.
    if (this.recebidoPorTransferencia(a)) return false;
    if (this.estaEstornado(a) || this.foiTransferido(a)) return false;
    return this.saldoExibido(a) > 0;
  }

  private ehOPacienteDaTela(codigo: string | null | undefined): boolean {
    return (codigo ?? '').trim() === String(this.paciente?.cdPessoaFisica ?? '').trim();
  }

  // Saiu deste paciente para outro.
  foiTransferido(a: PatientAdvance): boolean {
    if (this.transferidosConfirmados.has(a.nrAdiantamento)) return true;
    const destino = (a.cdPessoaAdiant ?? '').trim();
    return destino !== '' && !this.ehOPacienteDaTela(destino);
  }

  // "NOME — CPF", ou só o nome quando o cadastro não tem documento.
  identificacao(nome: string | null | undefined, cpf: string | null | undefined): string {
    const n = (nome ?? '').trim();
    const c = (cpf ?? '').trim();
    if (!n) return '—';
    return c ? `${n} — ${this.formatarCpf(c)}` : n;
  }

  // Chegou a este paciente vindo de outro: ele pode gastar, mas nao pagou.
  recebidoPorTransferencia(a: PatientAdvance): boolean {
    if (!a.cdPessoaFisica) return false;
    return !this.ehOPacienteDaTela(a.cdPessoaFisica) && this.ehOPacienteDaTela(a.cdPessoaAdiant);
  }

  abrirTransferencia(a: PatientAdvance): void {
    if (!this.podeTransferir(a) || this.isTransferindo) return;
    this.transferenciaAlvo = a;
    this.transferenciaTermo = '';
    this.transferenciaResultados = [];
    this.transferenciaDestino = null;
    this.erroTransferencia = '';
    this.limparTermo();
  }

  fecharTransferencia(): void {
    if (this.isTransferindo) return;
    if (this.transferenciaTimer) clearTimeout(this.transferenciaTimer);
    this.transferenciaAlvo = null;
    this.transferenciaResultados = [];
    this.transferenciaDestino = null;
    this.erroTransferencia = '';
    this.limparTermo();
  }

  // Um termo de uma sessao anterior nao vale: ele nomeia um destinatario que pode ter mudado.
  private limparTermo(): void {
    this.termoArquivo = null;
    this.termoAnexado = false;
    this.erroTermo = '';
  }

  onTermoDestinoChange(): void {
    this.limparTermo();
    if (this.transferenciaTimer) clearTimeout(this.transferenciaTimer);
    this.transferenciaDestino = null;

    const termo = this.transferenciaTermo.trim();
    if (termo.length < 3) {
      this.transferenciaResultados = [];
      return;
    }

    this.transferenciaTimer = setTimeout(async () => {
      this.isBuscandoDestino = true;
      try {
        const achados = await this.atendimentoService.buscarPaciente(termo);
        // O titular nunca aparece como destino: o back recusaria, e oferecer seria convidar ao erro.
        this.transferenciaResultados = achados.filter(
          p => String(p.cdPessoaFisica) !== String(this.paciente?.cdPessoaFisica),
        );
      } finally {
        this.isBuscandoDestino = false;
      }
    }, DEBOUNCE_BUSCA_MS);
  }

  selecionarDestino(p: PacienteBusca): void {
    this.transferenciaDestino = p;
    this.transferenciaResultados = [];
    this.transferenciaTermo = p.nmPessoaFisica;
  }

  imprimirTermoTransferencia(): void {
    const alvo = this.transferenciaAlvo;
    const destino = this.transferenciaDestino;
    if (!alvo || !destino) return;

    const dados: TermoTransferenciaImpresso = {
      nrAdiantamento: alvo.nrAdiantamento,
      valor: this.saldoExibido(alvo),
      origemNome: this.paciente?.nmPessoaFisica ?? '',
      origemCpf: this.formatarCpf(this.paciente?.nrCpf ?? ''),
      destinoNome: destino.nmPessoaFisica,
      destinoCpf: this.formatarCpf(destino.nrCpf ?? ''),
      logo: this.brandService.logo,
      cdEmpresa: this.brandService.cdEmpresa,
      marca: this.brandService.brandName,
    };

    try {
      openTermoWindow(`Termo ${alvo.nrAdiantamento}`, montarTermoTransferencia(dados));
      this.erroTermo = '';
    } catch {
      this.erroTermo = 'Erro ao gerar o termo de transferência.';
    }
  }

  onTermoSelecionado(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.termoArquivo = input.files?.[0] ?? null;
    this.termoAnexado = false;
    this.erroTermo = '';
  }

  async anexarTermo(): Promise<void> {
    const alvo = this.transferenciaAlvo;
    const destino = this.transferenciaDestino;
    if (!alvo || !this.termoArquivo || this.isAnexandoTermo) return;

    this.isAnexandoTermo = true;
    this.erroTermo = '';
    try {
      await this.paymentService.postTermoTransferencia(alvo.nrAdiantamento, this.termoArquivo, this.username);
      // O termo nomeia um destinatario: se o operador ja trocou de alvo/destinatario antes da
      // resposta voltar, esta anexagem nao vale para o par atual — descarta em silencio.
      if (this.transferenciaAlvo === alvo && this.transferenciaDestino === destino) {
        this.termoAnexado = true;
      }
    } catch (e: any) {
      if (this.transferenciaAlvo === alvo && this.transferenciaDestino === destino) {
        this.termoAnexado = false;
        this.erroTermo = e?.error?.notifications?.[0]?.message || e?.error?.feedback || e?.message || 'Não foi possível anexar o termo.';
      }
    } finally {
      this.isAnexandoTermo = false;
    }
  }

  temTermoParaConsultar(a: PatientAdvance): boolean {
    return this.foiTransferido(a) || this.recebidoPorTransferencia(a);
  }

  async abrirTermo(a: PatientAdvance): Promise<void> {
    if (this.isBuscandoTermo) return;
    const alvo = a.nrAdiantamento;

    this.isBuscandoTermo = true;
    this.buscandoTermoAlvo = alvo;
    this.erroTermoConsulta = '';
    this.erroTermoAlvo = null;
    try {
      const termo = await this.paymentService.getTermoTransferencia(alvo);
      if (!termo?.base64) {
        this.erroTermoConsulta = 'Termo não anexado para este adiantamento.';
        this.erroTermoAlvo = alvo;
        return;
      }

      const url = base64ParaBlobUrl(termo.base64);
      const aba = window.open(url, '_blank');
      if (!aba) {
        this.erroTermoConsulta = 'Pop-up bloqueado. Libere pop-ups para ver o termo.';
        this.erroTermoAlvo = alvo;
      }
      // A URL fica viva o suficiente para a aba carregar o PDF.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      this.erroTermoConsulta = e?.error?.notifications?.[0]?.message || e?.error?.feedback || e?.message || 'Não foi possível buscar o termo.';
      this.erroTermoAlvo = alvo;
    } finally {
      this.isBuscandoTermo = false;
      this.buscandoTermoAlvo = null;
    }
  }

  async confirmarTransferencia(): Promise<void> {
    const alvo = this.transferenciaAlvo;
    const destino = this.transferenciaDestino;
    if (!alvo || !destino || this.isTransferindo || !this.termoAnexado) return;

    this.isTransferindo = true;
    this.erroTransferencia = '';
    try {
      await this.paymentService.postTransferAdvance({
        nrAdiantamento: alvo.nrAdiantamento,
        cdPessoaDestino: String(destino.cdPessoaFisica),
        username: this.username,
      });

      this.ultimoComprovanteTransferencia = {
        nrAdiantamento: alvo.nrAdiantamento,
        valor: this.saldoExibido(alvo),
        origemNome: this.paciente?.nmPessoaFisica ?? '',
        origemCpf: this.formatarCpf(this.paciente?.nrCpf ?? ''),
        destinoNome: destino.nmPessoaFisica,
        destinoCpf: this.formatarCpf(destino.nrCpf ?? ''),
        dataHora: new Date().toISOString(),
        usuario: this.username,
        logo: this.brandService.logo,
        cdEmpresa: this.brandService.cdEmpresa,
        marca: this.brandService.brandName,
      };
      this.transferidosConfirmados.add(alvo.nrAdiantamento);

      this.transferenciaAlvo = null;
      this.transferenciaDestino = null;
      await this.recarregarAdiantamentosSilencioso();
    } catch (e: any) {
      this.erroTransferencia = e?.error?.notifications?.[0]?.message || e?.error?.feedback || e?.message || 'Não foi possível transferir o saldo.';
    } finally {
      this.isTransferindo = false;
      this.limparTermo();
    }
  }

  imprimirComprovanteTransferencia(): void {
    if (!this.ultimoComprovanteTransferencia) return;
    try {
      openPrintWindow(
        `Transferência ${this.ultimoComprovanteTransferencia.nrAdiantamento}`,
        montarComprovanteTransferencia(this.ultimoComprovanteTransferencia),
      );
      this.comprovanteTransferenciaErro = '';
    } catch {
      this.comprovanteTransferenciaErro = 'Erro ao gerar o comprovante de transferência.';
    }
  }

  // ── Estorno ────────────────────────────────────────────────

  // O back e a autoridade; aqui so nao se oferece a acao onde ela e invalida.
  podeEstornar(a: PatientAdvance): boolean {
    if (!this.isSupervisor) return false;
    // Devolver dinheiro e do pagador, nao de quem recebeu o saldo.
    if (this.recebidoPorTransferencia(a)) return false;
    if (this.estaEstornado(a)) return false;
    // Saldo intacto, não apenas positivo: com parte já aplicada a uma conta, o estorno devolveria
    // o recibo inteiro.
    if (!saldoIntacto(a.vlSaldo, a.vlAdiantamento)) return false;
    if (this.eDeEspecie(a)) return a.nrSeqCaixaRec != null && ehDeHoje(a.dtAdiantamento);
    return true;
  }

  brl(valor: number | null | undefined): string {
    return formatBRL(valor ?? 0);
  }

  saldoExibido(a: PatientAdvance): number {
    if (this.estaEstornado(a)) return 0;
    return a.vlSaldo > 0 ? a.vlSaldo : 0;
  }

  estaEstornado(a: PatientAdvance): boolean {
    return this.estornadosConfirmados.has(a.nrAdiantamento) || adiantamentoEstornado(a);
  }

  abrirConfirmEstorno(a: PatientAdvance): void {
    if (!this.podeEstornar(a) || this.isEstornando) return;
    this.estornoAlvo = a;
    this.erroEstorno = '';
    this.ultimoComprovanteEstorno = null;
    this.comprovanteEstornoErro = '';
  }

  fecharConfirmEstorno(): void {
    if (this.isEstornando) return;
    this.estornoAlvo = null;
    this.erroEstorno = '';
  }

  async confirmarEstorno(): Promise<void> {
    if (!this.estornoAlvo || this.isEstornando) return;
    const alvo = this.estornoAlvo;

    this.isEstornando = true;
    this.erroEstorno = '';
    try {
      if (this.eDeEspecie(alvo)) {
        if (alvo.nrSeqCaixaRec == null) {
          this.erroEstorno = 'Este recebimento não tem caixa de origem — estorne pelo sistema onde foi criado.';
          return;
        }
        await this.paymentService.postCashRefund({
          nrAdiantamento: alvo.nrAdiantamento,
          nrSeqCaixaRec: alvo.nrSeqCaixaRec,
          username: this.username,
        });
      } else {
        await this.paymentService.postAdyenRefund({
          nrAdiantamento: alvo.nrAdiantamento,
          username: this.username,
        });
      }

      // Captura antes de recarregar: o back pode demorar a gravar IE_STATUS/observacao.
      this.ultimoComprovanteEstorno = {
        nmPaciente: this.paciente?.nmPessoaFisica ?? '',
        cpf: this.formatarCpf(this.paciente?.nrCpf ?? ''),
        nrAdiantamento: alvo.nrAdiantamento,
        valor: alvo.vlSaldo,
        origem: this.origemDoAdiantamento(alvo),
        estabelecimento: this.nomeEstabelecimentoConhecido(alvo),
        dataHora: new Date().toISOString(),
        usuario: this.username,
        logo: this.brandService.logo,
        cdEmpresa: this.brandService.cdEmpresa,
        marca: this.brandService.brandName,
      };
      this.comprovanteEstornoErro = '';
      this.estornadosConfirmados.add(alvo.nrAdiantamento);

      this.estornoAlvo = null;
      await this.recarregarAtePosicaoDoEstorno(alvo.nrAdiantamento);
    } catch (e: any) {
      this.erroEstorno = e?.error?.feedback || e?.message || 'Erro ao estornar o pagamento.';
    } finally {
      this.isEstornando = false;
    }
  }

  // O POST do estorno volta quando a operadora aceita; a gravacao vem depois.
  private async recarregarAtePosicaoDoEstorno(nrAdiantamento: number): Promise<void> {
    await this.recarregarAdiantamentosSilencioso();

    for (const espera of ESPERAS_BUSCA_ADIANTAMENTO_MS) {
      const alvo = this.adiantamentos.find(a => a.nrAdiantamento === nrAdiantamento);
      if (!alvo || this.estaEstornado(alvo)) return;

      await this.aguardar(espera);
      if (!this.paciente) return;
      await this.recarregarAdiantamentosSilencioso();
    }
  }

  imprimirComprovanteEstorno(): void {
    if (!this.ultimoComprovanteEstorno || this.isImprimindoComprovanteEstorno) return;

    this.isImprimindoComprovanteEstorno = true;
    this.comprovanteEstornoErro = '';
    try {
      openPrintWindow(
        `Estorno ${this.ultimoComprovanteEstorno.nrAdiantamento}`,
        montarComprovanteEstorno(this.ultimoComprovanteEstorno),
      );
    } catch {
      this.comprovanteEstornoErro = 'Erro ao gerar o comprovante de estorno.';
    } finally {
      this.isImprimindoComprovanteEstorno = false;
    }
  }

  fecharComprovanteEstorno(): void {
    this.ultimoComprovanteEstorno = null;
    this.comprovanteEstornoErro = '';
  }

  // ── Comprovante ────────────────────────────────────────────

  get metodoEspecie(): boolean {
    return this.metodo === 4;
  }

  get metodoLabel(): string {
    switch (this.metodo) {
      case 1: return 'Crédito';
      case 2: return 'Débito';
      case 3: return 'Pix';
      default: return 'Dinheiro';
    }
  }

  // Os adiantamentos criados pela Fila gravam nrSeqCaixaRec mas nem sempre nrRecibo.
  eDeEspecie(a: PatientAdvance): boolean {
    return a.nrRecibo != null || a.nrSeqCaixaRec != null;
  }

  origemDoAdiantamento(a: PatientAdvance): 'Espécie' | 'Cartão/Pix' {
    return this.eDeEspecie(a) ? 'Espécie' : 'Cartão/Pix';
  }

  // Em cartao/Pix o adiantamento nasce na matriz financeira, nao na unidade do operador.
  private nomeEstabelecimentoConhecido(a: PatientAdvance): string {
    return this.caixas.find(c => c.establishmentId === a.cdEstabelecimento)?.establishmentName ?? '';
  }

  // O nrAdiantamento tem que vir do registro que a conferência confirmou — nunca inventado.
  get adiantamentoCartaoPix(): PatientAdvance | null {
    if (!this.referenceId) return null;
    const documento = extrairDocumentoDoReference(this.referenceId);
    if (!documento) return null;
    if (this.adiantamentoConfirmado?.nrDocumento === documento) return this.adiantamentoConfirmado;
    return this.adiantamentos.find(a => a.nrDocumento === documento) ?? null;
  }

  // Sem referenceId nao ha o que conferir (acontece no 504).
  get podeConferir(): boolean {
    return !!this.referenceId && !!extrairDocumentoDoReference(this.referenceId);
  }

  get podeImprimirComprovante(): boolean {
    if (this.estado !== 'confirmado') return false;
    return this.metodoEspecie ? !!this.reciboResultado : !!this.adiantamentoCartaoPix;
  }

  async imprimirComprovante(): Promise<void> {
    if (!this.podeImprimirComprovante || this.isImprimindoComprovante) return;

    this.isImprimindoComprovante = true;
    this.comprovanteErro = '';
    try {
      if (this.metodoEspecie) {
        await this.imprimirComprovanteEspecie();
      } else {
        this.imprimirComprovanteCartao();
      }
    } catch {
      this.comprovanteErro = 'Erro ao gerar o comprovante.';
    } finally {
      this.isImprimindoComprovante = false;
    }
  }

  private async imprimirComprovanteEspecie(): Promise<void> {
    const recibo = await this.paymentService.getCashReceipt(this.reciboResultado!.nrRecibo);
    if (!recibo) {
      this.comprovanteErro = 'Não foi possível carregar os dados do recibo.';
      return;
    }
    openPrintWindow(`Recibo ${recibo.nrRecibo}`, montarComprovanteEspecie({
      recibo,
      observacao: this.observacao,
      usuario: this.username,
      logo: this.brandService.logo,
      cdEmpresa: this.brandService.cdEmpresa,
      marca: this.brandService.brandName,
    }));
  }

  private imprimirComprovanteCartao(): void {
    const adiantamento = this.adiantamentoCartaoPix;
    if (!adiantamento) {
      this.comprovanteErro = 'Não foi possível localizar o adiantamento deste pagamento.';
      return;
    }
    const enviada = this.cobrancaEnviada;
    if (!enviada) {
      this.comprovanteErro = 'Não foi possível recuperar os dados desta cobrança.';
      return;
    }
    const dados: ComprovanteCartao = {
      nmPaciente: this.paciente?.nmPessoaFisica ?? '',
      cpf: this.formatarCpf(this.paciente?.nrCpf ?? ''),
      unidade: this.caixaSelecionada?.establishmentName ?? (this.localStorage.get('unitName') as string) ?? '',
      // Valor do adiantamento gravado, não o do input — que continua editável depois da confirmação.
      valor: adiantamento.vlAdiantamento,
      metodo: enviada.metodo,
      parcelas: enviada.parcelas,
      terminal: enviada.terminal,
      // Horário real da transação, não o instante em que o operador imprimiu.
      dataHora: adiantamento.dtAdiantamento,
      nrAdiantamento: adiantamento.nrAdiantamento,
      logo: this.brandService.logo,
      cdEmpresa: this.brandService.cdEmpresa,
      marca: this.brandService.brandName,
      usuario: this.username,
    };
    openPrintWindow(`Comprovante ${dados.nrAdiantamento}`, montarComprovanteCartao(dados));
  }

  // ── Reimpressão (histórico de adiantamentos) ──────────────────

  // Roteador: espécie tem recibo e vai pelo mesmo endpoint do comprovante original; cartão/Pix
  // não tem recibo — o comprovante é remontado só com o que o adiantamento guarda.
  async reimprimir(a: PatientAdvance): Promise<void> {
    if (!this.paciente || this.isReimprimindo) return;

    this.isReimprimindo = true;
    this.reimprimindoAlvo = a.nrAdiantamento;
    this.erroReimpressao = '';
    try {
      // this.adiantamentos pode estar desatualizado; reconfirma na fonte antes de imprimir.
      const listaAtual = await this.paymentService.getPatientAdvances(String(this.paciente.cdPessoaFisica), DIAS_HISTORICO_ADIANTAMENTOS);
      const atual = listaAtual.find(x => x.nrAdiantamento === a.nrAdiantamento) ?? a;
      const estornado = this.estaEstornado(atual);

      if (a.nrRecibo) {
        await this.reimprimirEspecie(a.nrRecibo, estornado);
      } else {
        this.reimprimirCartaoPix(a, estornado);
      }
    } catch {
      this.erroReimpressao = 'Erro ao gerar a 2ª via do comprovante.';
    } finally {
      this.isReimprimindo = false;
      this.reimprimindoAlvo = null;
    }
  }

  private async reimprimirEspecie(nrRecibo: number, estornado: boolean): Promise<void> {
    const recibo = await this.paymentService.getCashReceipt(nrRecibo);
    if (!recibo) {
      this.erroReimpressao = 'Não foi possível carregar os dados deste recibo.';
      return;
    }
    openPrintWindow(`Recibo ${recibo.nrRecibo} (2ª via)`, montarComprovanteEspecie({
      recibo,
      usuario: this.username,
      logo: this.brandService.logo,
      cdEmpresa: this.brandService.cdEmpresa,
      marca: this.brandService.brandName,
      segundaVia: true,
      estornado,
    }));
  }

  private reimprimirCartaoPix(a: PatientAdvance, estornado: boolean): void {
    openPrintWindow(`Comprovante ${a.nrAdiantamento} (2ª via)`, montarReimpressaoCartaoPix({
      nmPaciente: this.paciente?.nmPessoaFisica ?? '',
      cpf: this.formatarCpf(this.paciente?.nrCpf ?? ''),
      valor: a.vlAdiantamento,
      dataHora: a.dtAdiantamento,
      nrAdiantamento: a.nrAdiantamento,
      observacao: a.dsObservacao,
      usuario: this.username,
      logo: this.brandService.logo,
      cdEmpresa: this.brandService.cdEmpresa,
      marca: this.brandService.brandName,
      estornado,
    }));
  }

  formatarCpf(cpf: string | null | undefined): string {
    const d = (cpf ?? '').replace(/\D/g, '');
    if (d.length !== 11) return cpf || '—';
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
  }

  private limparTimeout(): void {
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = null;
    }
  }

  voltar(): void {
    if (this.embutido) {
      this.fechado.emit(null);
      return;
    }
    this.router.navigate(['/lancamentos-financeiros']);
  }

  // Guarda recusada: embutido avisa e fecha; na rota, redireciona como antes.
  private recusar(motivo: string, rota = '/lancamentos-financeiros'): void {
    if (this.embutido) {
      this.fechado.emit(motivo);
      return;
    }
    this.router.navigate([rota]);
  }
}
